import React from 'react';

// Utils
import {
  getDetailCustomFieldValue,
  isFieldForCategory,
  isFieldForListingType,
  pickCategoryFields,
  pickCustomFieldProps,
} from '../../util/fieldHelpers.js';

import CustomExtendedDataSection from '../../components/CustomExtendedDataSection/CustomExtendedDataSection.js';

// CUSTOM: Motoristpro — display category levels (incl. typed "Other" make/model values)
import { getSelectedCategoryLevels } from '../../util/customListingHelpers.js';

/**
 * Renders custom listing fields.
 * - SectionDetails is used if schemaType is 'enum', 'long', or 'boolean'
 * - SectionMultiEnum is used if schemaType is 'multi-enum'
 * - SectionText is used if schemaType is 'text'
 *
 * @param {*} props include publicData, metadata, listingFieldConfigs, categoryConfiguration
 * @returns React.Fragment containing aforementioned components
 */
const CustomListingFields = props => {
  const { publicData, metadata, listingFieldConfigs, categoryConfiguration, intl } = props;

  const { key: categoryPrefix, categories: listingCategoriesConfig } = categoryConfiguration;
  const categoriesObj = pickCategoryFields(publicData, categoryPrefix, 1, listingCategoriesConfig);
  const currentCategories = Object.values(categoriesObj);

  // Fields are shown by default. Set showConfig.displayOnListingPage to false to explicitly hide a field.
  const displayableFieldConfigs = listingFieldConfigs.filter(
    fieldConfig => fieldConfig.showConfig?.displayOnListingPage !== false
  );

  const isFieldForSelectedCategories = fieldConfig => {
    const isTargetCategory = isFieldForCategory(currentCategories, fieldConfig);
    return isTargetCategory;
  };
  const propsForCustomFields =
    pickCustomFieldProps(
      { publicData, metadata },
      displayableFieldConfigs,
      'listingType',
      isFieldForSelectedCategories
    ) || [];

  // CUSTOM: Motoristpro — prepend the selected category levels (vehicle type, make,
  // model) as detail rows. When an "Other" node is selected, the seller's typed
  // value (publicData.otherCategoryLevel<N>) is shown instead of the node name.
  // Labels come from ListingPage.categoryLevel<N>Label translations (e.g. "Make",
  // "Model") with ListingPage.categoryLabel as the fallback for deeper levels.
  const categoryDetailRows = getSelectedCategoryLevels(
    publicData,
    categoryPrefix,
    listingCategoriesConfig
  ).map(({ level, name }) => {
    const levelLabelId = `ListingPage.categoryLevel${level}Label`;
    const labelId = intl.messages[levelLabelId] ? levelLabelId : 'ListingPage.categoryLabel';
    return {
      key: `${categoryPrefix}${level}`,
      label: intl.formatMessage({ id: labelId }),
      value: name,
    };
  });
  // SectionDetails builds its rows with fieldConfigs.reduce(pickExtendedDataFields, []);
  // seeding the accumulator on the first iteration injects the category rows above
  // the configured listing fields without touching the shared template component.
  const pickExtendedDataFieldsWithCategories = (filteredConfigs, config, index) =>
    pickExtendedDataFields(
      index === 0 ? [...categoryDetailRows, ...filteredConfigs] : filteredConfigs,
      config
    );

  const sectionDetailsProps = {
    ...props,
    isFieldForCategory: isFieldForSelectedCategories,
    fieldConfigs: displayableFieldConfigs,
    heading: 'ListingPage.detailsTitle',
  };

  const pickExtendedDataFields = (filteredConfigs, config) => {
    const { key, schemaType, enumOptions, showConfig = {} } = config;
    const listingType = publicData.listingType;
    const isTargetListingType = isFieldForListingType(listingType, config);
    const isTargetCategory = isFieldForCategory(currentCategories, config);

    const { isDetail, label } = showConfig;
    const publicDataValue = publicData[key];
    const metadataValue = metadata[key];
    const value = publicDataValue != null ? publicDataValue : metadataValue;

    if (isDetail && isTargetListingType && isTargetCategory && typeof value !== 'undefined') {
      const detailValue = getDetailCustomFieldValue(
        enumOptions,
        value,
        schemaType,
        key,
        label,
        intl,
        'ListingPage'
      );

      return detailValue ? filteredConfigs.concat(detailValue) : filteredConfigs;
    }
    return filteredConfigs;
  };

  return (
    <CustomExtendedDataSection
      sectionDetailsProps={sectionDetailsProps}
      propsForCustomFields={propsForCustomFields}
      idPrefix="listingPage"
      // CUSTOM: Motoristpro — wrapped to prepend category (type/make/model) rows
      pickExtendedDataFields={pickExtendedDataFieldsWithCategories}
    />
  );
};

export default CustomListingFields;
